import React, { useState, useEffect, useRef } from 'react';
import { createConsumer } from '@rails/actioncable';

// RN の CHAT_TEMPLATES と揃える。タイトルと firstMessage を保持。
// B方針によりイベントスコープは廃止し、サークルスコープに統一。
const TEMPLATES = {
  circle_knowledge_creator: {
    title: 'サークルの魅力を教えてください',
    firstMessage:
      'こんにちは😀あなたのサークルのナレッジ登録をお手伝いします。\n' +
      'サークルの魅力や特徴をいくつかの観点からお聞きして、あなたのサークルに興味を持った方へのAI案内に活用します。\n' +
      'まずはじめに、サークルの一番の魅力や他にはない特長を教えていただけますか？',
  },
  circle_knowledge_user: {
    title: 'サークルについて質問する',
    firstMessage:
      'こんにちは😀このサークルについて気になることを聞いてください！\n' +
      '活動内容、雰囲気など、気になることがあればお気軽にきいてください。',
  },
};

// RN の ChatView.js + BottomSheetChatScreen.js を Web に移植したモーダル型チャット。
// 会話は chat_conversations テーブルで永続化され、モーダル再開時に履歴を復元する。
//
// TGW-1161: AI チャットは会員ログイン必須に統一(captchaToken Props 撤去)。
// セッション切れ等で 401 が返った場合は専用メッセージを表示する。
export default function CircleChat({
  templateType,
  circleId,
  canManage = false,
  onClose,
}) {
  const template = TEMPLATES[templateType] || TEMPLATES.circle_knowledge_user;

  // null: 履歴取得中（スピナー表示）、配列: 表示可能
  const [messages, setMessages] = useState(null);
  const [chatConversationId, setChatConversationId] = useState(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const consumerRef = useRef(null);
  const subRef = useRef(null);
  const messageListRef = useRef(null);
  // POST成功直後に自分のユーザーメッセージを手動追加した直後、
  // サーバーのbroadcast(start)が来るまでの間、他のイベントを無視するためのフラグ。
  const isSettingPostedMessage = useRef(false);
  // ActionCable WebSocket が未確立な環境でも UI を更新するための polling fallback。
  // 送信後、ChatReplyJob 完了を履歴 API で検出してローディングを解除する。
  const pollingTimerRef = useRef(null);
  const loadingRef = useRef(false);

  const buildFirstMessageArr = () =>
    template.firstMessage
      ? [
          {
            id: `first-${Date.now()}`,
            role: 'assistant',
            content: template.firstMessage,
          },
        ]
      : [];

  // 履歴取得 - mount時およびテンプレ/scope切替時に会話履歴を取得して復元する
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const params = new URLSearchParams({ template_type: templateType });
        if (circleId) {
          params.set('circle_id', String(circleId));
        }
        const res = await fetch(
          `/api/chat_conversations/messages?${params.toString()}`,
          {
            method: 'GET',
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
          },
        );
        if (!res.ok) {
          throw new Error(`getChatMessages status=${res.status}`);
        }
        const payload = await res.json();
        if (cancelled) return;
        const { chat_conversation, chat_messages } = payload?.data ?? {};
        setChatConversationId(chat_conversation?.external_id ?? null);
        if (
          chat_conversation &&
          Array.isArray(chat_messages) &&
          chat_messages.length > 0
        ) {
          setMessages(chat_messages);
        } else {
          setMessages(buildFirstMessageArr());
        }
      } catch (e) {
        if (cancelled) return;
        // 履歴取得に失敗しても firstMessage で会話を開始できるようにする
        // eslint-disable-next-line no-console
        console.error('getChatMessages failed', e);
        setMessages(buildFirstMessageArr());
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateType, circleId]);

  // メッセージ追加後に末尾までスクロール
  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [messages]);

  // ActionCable 購読 - chatConversationId が確定してから購読を開始
  useEffect(() => {
    if (!chatConversationId) return undefined;

    // Web では Devise セッション cookie で WebSocket を認証するため token 不要
    const consumer = createConsumer();
    consumerRef.current = consumer;

    // サーバの ChatChannel は params[:chat_conversation_id] を期待する (chat_channel.rb 参照)
    // Backend broadcast channel: "chat_#{chat_conversation.external_id}"
    subRef.current = consumer.subscriptions.create(
      {
        channel: 'ChatChannel',
        chat_conversation_id: chatConversationId,
      },
      {
        received(data) {
          if (isSettingPostedMessage.current) {
            return;
          }
          const type = data?.type;
          const assistantMessage = data?.assistant_message;
          if (type === 'error') {
            const content =
              data?.assistant_message?.content || 'エラーが発生しました';
            setMessages((prev) => [
              ...(prev ?? []),
              { role: 'assistant', content, isError: true },
            ]);
            setLoading(false);
            return;
          }
          if (data?.error && !type) {
            setMessages((prev) => [
              ...(prev ?? []),
              { role: 'assistant', content: data.error, isError: true },
            ]);
            setLoading(false);
            return;
          }
          if (!assistantMessage) {
            return;
          }
          if (type === 'start') {
            setMessages((prev) => [
              ...(prev ?? []),
              { ...assistantMessage, isGenerating: true },
            ]);
          } else if (type === 'delta') {
            const { id, content } = assistantMessage;
            if (id == null) return;
            setMessages((prev) => {
              if (!prev) return prev;
              if (prev.findIndex((m) => m?.id === id) === -1) {
                return [...prev, { ...assistantMessage, isGenerating: true }];
              }
              return prev.map((m) => (m?.id === id ? { ...m, content } : m));
            });
          } else if (type === 'completed') {
            const { id, content } = assistantMessage;
            if (id == null) return;
            setMessages((prev) => {
              if (!prev) return prev;
              if (prev.findIndex((m) => m?.id === id) === -1) {
                return [...prev, { ...assistantMessage, isGenerating: false }];
              }
              return prev.map((m) =>
                m?.id === id ? { ...m, content, isGenerating: false } : m,
              );
            });
            setLoading(false);
          }
        },
      },
    );

    return () => {
      if (subRef.current) {
        try {
          subRef.current.unsubscribe();
        } catch (_e) {
          // no-op
        }
      }
      if (consumerRef.current) {
        try {
          consumerRef.current.disconnect();
        } catch (_e) {
          // no-op
        }
      }
    };
  }, [chatConversationId]);

  const csrfToken = () => {
    const el = document.querySelector('meta[name="csrf-token"]');
    return el ? el.content : '';
  };

  // loading 値を closure ではなく ref 経由で参照するための同期
  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  // unmount 時に進行中の polling timer をクリア
  useEffect(
    () => () => {
      if (pollingTimerRef.current) {
        clearTimeout(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    },
    [],
  );

  // ActionCable WebSocket が未確立な環境（nginx の Upgrade ヘッダ proxy 設定欠落等）
  // でも UI を更新するための polling fallback。
  // 送信後 2 秒待ってから 1.5 秒間隔で履歴 API を叩き、新しい assistant メッセージを検出する。
  // ActionCable 経由で `setLoading(false)` されたら自動的に停止する。
  const startReplyPollingFallback = (afterMessageId) => {
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }

    let attempts = 0;
    const MAX_ATTEMPTS = 30; // 30 ticks ≒ 45秒で諦める

    const tick = async () => {
      // ActionCable で完了済みなら何もしない
      if (!loadingRef.current) {
        pollingTimerRef.current = null;
        return;
      }
      attempts += 1;

      try {
        const params = new URLSearchParams({ template_type: templateType });
        if (circleId) params.set('circle_id', String(circleId));
        const res = await fetch(
          `/api/chat_conversations/messages?${params.toString()}`,
          {
            method: 'GET',
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
          },
        );
        if (res.ok) {
          const payload = await res.json();
          const remoteMessages = payload?.data?.chat_messages || [];
          const newAssistant = remoteMessages.find(
            (m) =>
              typeof m.id === 'number' &&
              typeof afterMessageId === 'number' &&
              m.id > afterMessageId &&
              m.role === 'assistant' &&
              typeof m.content === 'string' &&
              m.content.trim().length > 0,
          );
          if (newAssistant && loadingRef.current) {
            setMessages(remoteMessages);
            setLoading(false);
            pollingTimerRef.current = null;
            return;
          }
        }
      } catch (_e) {
        // ネットワーク一時エラーは次の tick で再試行
      }

      if (attempts >= MAX_ATTEMPTS) {
        // 諦めて loading を解除（ユーザーは画面リロードで履歴復元可能）
        if (loadingRef.current) {
          setLoading(false);
          setMessages((prev) => [
            ...(prev ?? []),
            {
              role: 'assistant',
              content:
                '応答の取得に時間がかかっています。画面を再読み込みすると最新のメッセージが表示されます。',
              isError: true,
            },
          ]);
        }
        pollingTimerRef.current = null;
        return;
      }

      pollingTimerRef.current = setTimeout(tick, 1500);
    };

    pollingTimerRef.current = setTimeout(tick, 2000);
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading || messages === null) return;

    isSettingPostedMessage.current = true;
    setLoading(true);

    const body = {
      template_type: templateType,
      scope: { type: 'circle', id: circleId },
      mode: canManage ? 'learn' : 'user',
      // external_id は既存会話のルックアップまたは新規作成時のIDとして使う。
      // null の場合はサーバーが自動採番する。
      external_id: chatConversationId,
      question: text,
    };

    try {
      const res = await fetch('/api/chat_conversations/post_message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken(),
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify(body),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        isSettingPostedMessage.current = false;
        // TGW-1161: 401 はセッション切れ等の未ログイン状態。
        // ログイン誘導メッセージを表示する。
        const isUnauthorized = res.status === 401;
        const errorContent = isUnauthorized
          ? 'セッションが切れた可能性があります。お手数ですが、再度ログインしてからお試しください。'
          : payload.error ||
            `メッセージの送信に失敗しました (status=${res.status})`;
        setMessages((prev) => [
          ...(prev ?? []),
          {
            role: 'assistant',
            content: errorContent,
            isError: true,
          },
        ]);
        setLoading(false);
        return;
      }
      const { chat_conversation, chat_message } = payload?.data ?? {};
      if (!chat_conversation || !chat_message) {
        throw new Error('invalid chat response');
      }
      setChatConversationId(chat_conversation.external_id);
      setMessages((prev) => [...(prev ?? []), chat_message]);
      setInput('');
      isSettingPostedMessage.current = false;
      // ActionCable WebSocket が未確立な環境向けの fallback
      startReplyPollingFallback(chat_message.id);
    } catch (e) {
      isSettingPostedMessage.current = false;
      setMessages((prev) => [
        ...(prev ?? []),
        {
          role: 'assistant',
          content: 'メッセージの送信に失敗しました',
          isError: true,
        },
      ]);
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && onClose) {
      onClose();
    }
  };

  const isValid = input.trim().length > 0 && !loading && messages !== null;

  return (
    <div
      className="cchat-modal-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
    >
      <div className="cchat-modal">
        <div className="cchat-modal-header">
          <button
            type="button"
            className="cchat-close-btn"
            onClick={onClose}
            aria-label="閉じる"
          >
            ×
          </button>
          <div className="cchat-ai-icon" aria-hidden="true">
            🤖
          </div>
          <div className="cchat-header-title">
            <div className="cchat-header-label">AIアシスタント</div>
            <div className="cchat-header-title-text">『{template.title}』</div>
          </div>
        </div>

        {messages === null ? (
          <div className="cchat-loading" role="status" aria-label="読み込み中">
            <div className="cchat-spinner" />
          </div>
        ) : (
          <div className="cchat-messages" ref={messageListRef}>
            {messages.map((m, i) => (
              <div
                key={m.id || i}
                className={`cchat-msg-row cchat-msg-row-${m.role}`}
              >
                {m.role !== 'user' && (
                  <div className="cchat-msg-avatar" aria-hidden="true">
                    🤖
                  </div>
                )}
                <div
                  className={`cchat-msg-bubble${m.isError ? ' cchat-msg-bubble-error' : ''}`}
                >
                  {m.content}
                  {m.isGenerating ? ' ...🤖' : ''}
                </div>
              </div>
            ))}
            {loading &&
              messages.length > 0 &&
              messages[messages.length - 1]?.role === 'user' && (
                <div
                  className="cchat-msg-row cchat-msg-row-assistant"
                  role="status"
                  aria-label="AIが考えています"
                >
                  <div className="cchat-msg-avatar" aria-hidden="true">
                    🤖
                  </div>
                  <div className="cchat-msg-bubble cchat-msg-bubble-thinking">
                    <span className="cchat-typing-dot" />
                    <span className="cchat-typing-dot" />
                    <span className="cchat-typing-dot" />
                  </div>
                </div>
              )}
          </div>
        )}

        <div className="cchat-input-area">
          <div className="cchat-input-box">
            <textarea
              className="cchat-textarea"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              placeholder=""
              disabled={messages === null}
            />
            <button
              type="button"
              className="cchat-submit-btn"
              onClick={handleSend}
              disabled={!isValid}
              aria-label="送信"
            >
              ↑
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
